import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { mediaUrl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Building2, CalendarClock, Users, FileText, DollarSign, Printer } from "lucide-react";

const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("en", { weekday: "short", month: "short", day: "numeric" });

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-border rounded-sm p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground"><Icon className="w-4 h-4" />{label}</div>
      <div className="font-display text-2xl font-semibold mt-2">{value}</div>
    </div>
  );
}

export default function BusinessDashboard() {
  const [data, setData] = useState(null);
  const [invoice, setInvoice] = useState(null);

  useEffect(() => { api.get("/business/dashboard").then((r) => setData(r.data)); }, []);
  const openInvoice = async (id) => {
    const { data } = await api.get(`/business/invoices/${id}`);
    setInvoice(data);
  };
  if (!data) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-sm bg-primary/10 flex items-center justify-center"><Building2 className="w-6 h-6 text-primary" /></div>
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="business-org-name">{data.org_name || "Business"}</h1>
          <p className="text-muted-foreground text-sm">Your organization's booking hub</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat icon={CalendarClock} label="Upcoming" value={data.summary.upcoming_count} />
        <Stat icon={Users} label="Vendors" value={data.summary.vendor_count} />
        <Stat icon={FileText} label="Invoices" value={data.summary.total_invoices} />
        <Stat icon={DollarSign} label="Total spent" value={`$${data.summary.total_spent}`} />
      </div>

      <Tabs defaultValue="calendar" className="mt-10">
        <TabsList className="rounded-sm">
          <TabsTrigger value="calendar" className="rounded-sm" data-testid="biz-tab-calendar">Calendar</TabsTrigger>
          <TabsTrigger value="vendors" className="rounded-sm" data-testid="biz-tab-vendors">Vendors</TabsTrigger>
          <TabsTrigger value="invoices" className="rounded-sm" data-testid="biz-tab-invoices">Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-6">
          {data.calendar.length === 0 ? (
            <p className="text-muted-foreground" data-testid="biz-calendar-empty">No upcoming visits. <Link to="/browse" className="text-primary">Browse services</Link>.</p>
          ) : (
            <div className="bg-white border border-border rounded-sm divide-y divide-border">
              {data.calendar.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-4" data-testid={`biz-cal-${i}`}>
                  <div>
                    <div className="font-medium">{c.title}</div>
                    <div className="text-sm text-muted-foreground">{c.provider_name} · {c.kind}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">{fmtDate(c.date)}</div>
                    <div className="text-sm text-muted-foreground">{c.time}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="vendors" className="mt-6">
          {data.vendors.length === 0 ? (
            <p className="text-muted-foreground" data-testid="biz-vendors-empty">No vendors yet.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {data.vendors.map((v) => (
                <div key={v.provider_id} className="bg-white border border-border rounded-sm p-5 flex items-center justify-between" data-testid={`biz-vendor-${v.provider_id}`}>
                  <div className="flex items-center gap-3">
                    {v.logo_url ? <img src={mediaUrl(v.logo_url)} alt="" className="w-10 h-10 rounded-full object-cover border border-border" /> : <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center"><Users className="w-5 h-5 text-muted-foreground" /></div>}
                    <div>
                      <Link to={`/p/${v.provider_id}`} className="font-medium hover:text-primary hover:underline">{v.provider_name}</Link>
                      <div className="text-xs text-muted-foreground">{v.bookings_count} booking(s){v.city ? ` · ${v.city}` : ""}</div>
                    </div>
                  </div>
                  <div className="font-display text-lg font-semibold text-primary">${v.total_spent}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="invoices" className="mt-6">
          {data.invoices.length === 0 ? (
            <p className="text-muted-foreground" data-testid="biz-invoices-empty">No invoices yet. Paid bookings will appear here.</p>
          ) : (
            <div className="bg-white border border-border rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr><th className="p-4">Invoice</th><th className="p-4">Service</th><th className="p-4">Provider</th><th className="p-4">Date</th><th className="p-4">Amount</th><th className="p-4"></th></tr>
                </thead>
                <tbody>
                  {data.invoices.map((inv) => (
                    <tr key={inv.id} className="border-t border-border" data-testid={`biz-invoice-${inv.id}`}>
                      <td className="p-4 font-mono text-xs">{inv.number}</td>
                      <td className="p-4">{inv.service_title}{inv.is_recurring_visit && <span className="ml-1.5 text-[10px] uppercase font-bold text-primary bg-primary/10 rounded-sm px-1.5 py-0.5">recurring</span>}</td>
                      <td className="p-4">{inv.provider_name}</td>
                      <td className="p-4 text-muted-foreground">{inv.date}</td>
                      <td className="p-4 font-medium text-primary">${inv.amount}</td>
                      <td className="p-4 text-right"><Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => openInvoice(inv.id)} data-testid={`view-invoice-${inv.id}`}>View</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!invoice} onOpenChange={(o) => !o && setInvoice(null)}>
        <DialogContent data-testid="invoice-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Invoice {invoice?.number}</DialogTitle></DialogHeader>
          {invoice && (
            <div className="text-sm" id="invoice-print">
              <div className="flex justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Billed to</p>
                  <p className="font-medium mt-1">{invoice.issued_to.org_name}</p>
                  <p className="text-muted-foreground">{invoice.issued_to.name} · {invoice.issued_to.email}</p>
                  <p className="text-muted-foreground">Tax ID: {invoice.issued_to.tax_id}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">From</p>
                  <p className="font-medium mt-1">{invoice.from.provider_name}</p>
                  <p className="text-muted-foreground">{invoice.from.city}</p>
                  <span className="inline-block mt-2 text-xs font-bold text-green-800 bg-green-100 rounded-sm px-2 py-0.5">{invoice.status}</span>
                </div>
              </div>
              <table className="w-full mt-6 border-t border-border">
                <thead><tr className="text-xs uppercase text-muted-foreground"><th className="py-2 text-left">Description</th><th className="py-2 text-right">Amount</th></tr></thead>
                <tbody>
                  {invoice.line_items.map((li, i) => (
                    <tr key={i} className="border-t border-border"><td className="py-2">{li.description}<div className="text-xs text-muted-foreground">{li.date} {li.time}</div></td><td className="py-2 text-right">${li.amount}</td></tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between mt-4 pt-4 border-t border-border font-display text-lg font-semibold"><span>Total</span><span className="text-primary">${invoice.total}</span></div>
              <Button className="rounded-sm w-full mt-6" onClick={() => window.print()} data-testid="print-invoice"><Printer className="w-4 h-4 mr-2" />Print / Save PDF</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
